import { z } from "zod/v4";

import { createEndpointBase } from "./core";

const base = "ai-optimizer";
const aiOptimizer = createEndpointBase(base);

const optimizationHistoryItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  createdAt: z.string(),
  status: z.string(),
  integrationMetadata: z
    .object({
      optimization: z
        .object({
          changesSummary: z.string().optional(),
          tokenUsage: z
            .object({
              promptTokens: z.number(),
              completionTokens: z.number(),
              totalTokens: z.number(),
              cachedInputTokens: z.number().optional(),
            })
            .optional(),
          costEstimate: z
            .object({
              inputCost: z.number(),
              outputCost: z.number(),
              cachedInputCost: z.number(),
              totalCost: z.number(),
              currency: z.string(),
              modelId: z.string(),
            })
            .optional(),
          summary: z.string().optional(),
          success: z.boolean().optional(),
          verificationDescription: z.string().optional(),
          durationMs: z.number().optional(),
        })
        .optional(),
    })
    .nullable()
    .optional(),
});

export const aiOptimizerEndpoints = {
  optimizationHistory: aiOptimizer({
    method: "GET",
    path: "history/:scriptId",
    request: z.object({ scriptId: z.coerce.number() }),
    response: z.array(optimizationHistoryItemSchema),
  }),
  replaceSteps: aiOptimizer({
    method: "POST",
    path: "replace-steps",
    request: z.object({
      sourceScriptId: z.number(),
      targetScriptId: z.number(),
    }),
    response: z.object({ success: z.boolean() }),
  }),
  optimizeScriptStream: aiOptimizer({
    method: "POST",
    path: "optimize-stream",
    request: z.object({
      scriptId: z.coerce.number(),
      interactive: z.boolean().default(false),
      maxSteps: z.number().default(20),
      runTestExecution: z.boolean().optional(),
      executionTimeoutMs: z.number().default(1000 * 60 * 60),
      maxOptimizationIterations: z.number().default(10),
      useVideo: z.boolean().default(false),
      useUniqueTestData: z.boolean().default(true),
      verifySteps: z.boolean().default(true),
      prompt: z.string().optional(),
      continueFromScriptId: z.number().optional(),
      continuationMessage: z.string().optional(),
      contextDocuments: z
        .array(
          z.object({
            name: z.string().max(255),
            mimeType: z.string(),
            data: z.string(),
          }),
        )
        .optional(),
    }),
    response: z.any(),
  }),
  answerQuestion: aiOptimizer({
    method: "POST",
    path: "answer-question",
    request: z.object({
      scriptId: z.coerce.number(),
      questionId: z.string(),
      answer: z.string(),
    }),
    response: z.object({ success: z.boolean() }),
  }),
} as const;
